import React, { useState } from "react";
import { Icon, IconTextInput } from "../../../_components";

export function EventsBasics() {
  const [demoIcon, setDemoIcon] = useState("");

  const handleDemoIcon = (ev) => {
    // console.log(ev);
    const { value } = ev.target;
    setDemoIcon(value);
  };

  return (
    <div className="chronos-help-topic-page chronos-events-basics-topic chronos-form">
      <p>
        In Chronicles, an Event is an entry on your Timeline. You must create a
        Timeline before you can create Events. You can also link your event to
        one of your maps; when selected, the location on the map will zoom and
        center, showing where your event happened.
      </p>
      <p>
        Events can be anything - a battle, war, birth, invention, cataclysm, or
        the founding of an awesome worldbuilding platform{" "}
        <Icon
          icon="fas fa-question"
          data-tip="October 2017, if you're curious!"
          data-for="chronos-help-tooltip"
          color="red"
        />
        . For more ideas, scroll through the Event Type dropdown in the Event
        creation/edit interface.
      </p>
      <p>
        When you (or a viewer) left click an event, a small pop-up appears with
        a cover image, title, duration, and an excerpt. Clicking "Read the full
        story" opens the event in the left side panel, showing all your Event
        information.
      </p>
      <h3 className="chronos-topic-header">Creating an Event</h3>
      <p>To create a new event, either:</p>
      <ol>
        <li>
          Right click on timeline and select "<Icon icon="fas fa-plus fa-fw" />
          &nbsp;Create New Event". This method automatically selects that time
          and timeline for your new Event, but you can edit this.
        </li>
        <li>
          Left click on the green Plus Button (right hand side, above the
          timelines) and select "<Icon icon="fas fa-plus fa-fw" />
          &nbsp;Add Event"
        </li>
      </ol>
      <p>Read more in "Editing an Event" for your Event options!</p>
      <h3 className="chronos-topic-header">Deleting an Event</h3>
      <p>
        Delete your Event by right clicking it on the Timeline, selecting the
        Delete option, and reconfirming on the Confirm Deletion window. There is
        also a Delete option on the bottom left of the Edit Event window.
      </p>
      <p>
        Deleting an Event is an irreversible action, and all your descriptions
        and data will be lost.
      </p>
      <h3 className="chronos-topic-header">Editing an Event</h3>
      <p>
        Edit your Event by right clicking the Event icon on the Timeline, and
        selecting the "Edit" option.
      </p>
      <p>
        There are four tabs on the top of the Edit interface:{" "}
        <strong>General</strong>, <strong>Display</strong>,{" "}
        <strong>Privacy</strong> and <strong>Links</strong>.
      </p>
      <h3 className="chronos-topic-header">General Tab</h3>
      <h4 className="chronos-topic-subheader">Title, Excerpt, Description</h4>
      <p>
        The <strong>Title</strong> appears when you hover over an Event in the
        timeline, and whenever the event is selected. The{" "}
        <strong>Excerpt</strong> appears only in the small Event pop up, and the{" "}
        <strong>Long Description</strong> appears only in the extended side
        panel view.
      </p>
      <h4 className="chronos-topic-subheader">Start and End Dates</h4>
      <p>
        All dates refer to your custom Chronicles calendar. The{" "}
        <strong>Start</strong> date for your Event is obligatory. If an Event
        has an <strong>End</strong> date (which must be after the Start date!),
        it may appear as an icon or a "spanned" block, depending on the duration
        of the event and the Granularity you're zoomed in on.
      </p>
      <p>
        For example, a day-long event appears as an icon when the time unit
        Granularity is "Years" or "Days", but as a "spanned" block when the time
        unit Granularity is "Hours".
      </p>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
          flexWrap: "nowrap",
        }}
      >
        {/* Image UUID: 87167273-ba14-49eb-8fcb-7ef5047051dd */}
        <img
          src="https://worldanvil-chronos.nyc3.digitaloceanspaces.com/default/images/chronos-event-icon.png"
          alt="A day-long Event, when Granularity is set to Days"
        />
        {/* Image UUID: 99c82529-01dc-4004-a2f5-ccc045937792 */}
        <img
          src="https://worldanvil-chronos.nyc3.digitaloceanspaces.com/default/images/chronos-event-span.png"
          alt="Same Event, but on Hours Granularity."
        />
      </div>
      <span className="chronos-help-text text-center">
        The same Event, viewed at a Granularity of Days and Hours
      </span>
      <p>
        If an Event only has a start date, it will appear as an icon no matter
        how close in time you zoom.
      </p>
      <h4 className="chronos-topic-subheader">Event Type and Significance</h4>
      <p>
        <strong>Event Type</strong> determines which large, thematic icon
        appears on the small Event pop up. <strong>Significance</strong>{" "}
        determines which icon indicates the Event on the timeline. You can
        further customize which icon displays on the Timeline under the{" "}
        <strong>Display</strong> tab.
      </p>
      <h4 className="chronos-topic-subheader">
        Timeline, Timeline Lane, and Map Marker
      </h4>
      <p>
        With these options, you can specify where and when your event happened!
      </p>
      <p>
        <strong>Timeline</strong> and <strong>Timeline Lane</strong> allows you
        to specify exactly where the event should appear. For more on Timeline
        Lanes, read the Timeline section.
      </p>
      <p>
        The <strong>Map Marker</strong> dropdown will show you all the map
        markers you've added to all the maps in your Chronicle. When you click
        on your event, the map section will pan to your chosen Map and Map
        Marker.
      </p>
      <h4 className="chronos-topic-subheader">Tags</h4>
      <p>
        If there are important keywords you didn't add in the description, add
        them in tags. This will help both you and your viewers find relevant
        events.
      </p>
      <h3 className="chronos-topic-header">Display Tab</h3>
      <p>
        In the Cover Image section, you can upload a new cover image especially
        for this Event, or choose from an Existing cover image, already used
        elsewhere in your Chronicle.
      </p>
      <p>
        Under <strong>Timeline Display Color</strong>, you can change the colour
        of the event's icon on the timeline. If the event is a "spanning" Event
        with extended duration, the bar will be this colour too.
      </p>
      <p>
        In <strong>Timeline Display Icon</strong>, you can select a custom icon
        for your Event for when it is displayed in the Timeline. Select an icon
        from either{" "}
        <a href="https://fontawesome.com/v5/search" target="_blank">
          FontAwesome
        </a>{" "}
        or{" "}
        <a
          href="https://nagoshiashumari.github.io/Rpg-Awesome/"
          target="_blank"
        >
          RPG Awesome
        </a>
        , then input the full name (with an additional <strong>fa</strong> or{" "}
        <strong>ra</strong> respectively at the beginning) in this field. If the
        icon appears in the left hand box field, then the system has found your
        icon. Otherwise, double check your code!
      </p>
      <span>For example, try some of these:</span>
      <ul>
        <li>
          <strong>fa fa-hourglass</strong> for a FontAwesome Hourglass{" "}
          <i className="fa fa-hourglass" />
        </li>
        <li>
          <strong>fa fa-dragon</strong> for a FontAwesome Dragon{" "}
          <i className="fa fa-dragon" />
        </li>
        <li>
          <strong>ra ra-dragon</strong> for a RPG Awesome Dragon{" "}
          <i className="ra ra-dragon" />
        </li>
        <li>
          <strong>ra ra-sword</strong> for a RPG Awesome Sword{" "}
          <i className="ra ra-sword" />
        </li>
      </ul>
      <div className="form-group">
        <label htmlFor="demoIcon" className="chronos-form-title">
          Try it!
        </label>

        <IconTextInput
          id="demoIcon"
          value={demoIcon}
          onChange={handleDemoIcon}
        />
      </div>
      <h3 className="chronos-topic-header">Privacy</h3>
      <p>
        Under the Privacy tab, you can make your Event private, so that viewers
        cannot see it. Once Private, you can select Subscriber groups, to give
        exclusive viewing access to certain people.
      </p>
      <p>
        To learn more about subscriber groups, read the{" "}
        <a
          href="https://www.worldanvil.com/w/WorldAnvilCodex/a/subscribers"
          target="_blank"
        >
          Codex
        </a>
        .
      </p>
      <h3 className="chronos-topic-header">Links</h3>
      <p>
        <strong>Links</strong>, the final Edit Event tab, allows you to link one
        or multiple World Anvil articles to your Event. Click{" "}
        <strong>
          <i className="fa fa-plus" />
          &nbsp;Link Article
        </strong>{" "}
        to reveal a search bar, then simply search the articles in your world
        and click on the ones you want to link.
      </p>
      <p>
        These articles appear at the bottom of the full view (left panel
        display) of your event as article blocks.
      </p>
      <p>
        Linked articles are usually Event-related aspects that give your viewers
        more information or context. For example, if your Event is a birth, you
        might include the character article, and their parents' character
        articles. If it's the founding of the city, it might be the settlement
        article, and the city founder's character article.
      </p>
      <h3 className="chronos-topic-header">Events inside other Events</h3>
      <p>
        Often a long Event (e.g. a war, or a lifetime) has several small events
        inside it (e.g. battles/sieges, or promotions/weddings). Place all these
        events on the same Timeline Lane in Chronicles, and they will create a
        mini-timeline for you! This can viewed in the full display on the left
        hand panel.
      </p>
    </div>
  );
}
